/**
 * /posh/about — "Policy & Committee" for EVERY employee (POSH Act 2013
 * §19(a)/(b)): read the company's POSH policy and confirm it, see who is
 * on the Internal Committee today and how to reach them, and how to raise
 * a concern. Follows the person's POSH language (English / Hindi).
 *
 * Data: GET /posh/my (normal sign-in, no 2FA); the committee is read live
 * on the server, never from the frozen policy text.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import {
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  MessageSquareWarning,
  Phone,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { saveBlobAs } from "@/lib/save-blob";
import { usePoshLang } from "@/lib/posh-lang";
import { downloadPoshMyPolicyPdf, type PoshMyPolicy, type PoshMyResponse } from "@/lib/api/posh-my";
import { useAckPoshPolicy, usePoshMy } from "@/hooks/use-posh-my";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { TrainingLanguageSwitch } from "@/components/posh/training-language-switch";
import { aboutText, committeeRoleText, telHref } from "@/components/posh/posh-about-text";
import type { PoshLang } from "@/components/posh/posh-ui-text";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/about")({
  component: PoshAboutRoute,
});

function PoshAboutRoute() {
  const { lang } = usePoshLang();
  const q = usePoshMy();

  return (
    <PoshModuleShell
      title={aboutText("pageTitle", lang)}
      description={aboutText("pageDescription", lang)}
      actions={<TrainingLanguageSwitch />}
    >
      {q.isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : q.isError || !q.data ? (
        <Alert variant="destructive">
          <AlertDescription>{aboutText("loadError", lang)}</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-6" lang={lang}>
          <PolicyCard policy={q.data.policy} lang={lang} />
          <CommitteeCard committee={q.data.committee} lang={lang} />
          <ConcernCard links={q.data.links} lang={lang} />
        </div>
      )}
    </PoshModuleShell>
  );
}

function PolicyCard({ policy, lang }: Readonly<{ policy: PoshMyPolicy | null; lang: PoshLang }>) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const ack = useAckPoshPolicy();

  if (!policy) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText aria-hidden className="h-4 w-4 text-primary" />
            {aboutText("policyHeading", lang)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground" data-testid="posh-about-no-policy">
            {aboutText("noPolicy", lang)}
          </p>
        </CardContent>
      </Card>
    );
  }

  const onDownload = async () => {
    setDownloading(true);
    try {
      saveBlobAs(await downloadPoshMyPolicyPdf(), "posh-policy.pdf");
    } catch (err) {
      toast.error(toApiError(err).message || aboutText("ackError", lang));
    } finally {
      setDownloading(false);
    }
  };

  const onAck = () => {
    ack.mutate(policy.id, {
      onSuccess: () => toast.success(aboutText("ackSaved", lang)),
      onError: (err) =>
        toast.error(
          toApiError(err).error === "policy_not_current"
            ? aboutText("ackStale", lang)
            : aboutText("ackError", lang),
        ),
    });
  };

  const meta = [
    policy.published_at ? aboutText("policyPublished", lang, { date: formatDate(policy.published_at) }) : "",
    aboutText("policyVersion", lang, { version: policy.version }),
  ]
    .filter(Boolean)
    .join(" · ");
  const englishNote = aboutText("policyEnglishNote", lang);

  return (
    <Card data-testid="posh-about-policy">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText aria-hidden className="h-4 w-4 text-primary" />
          {aboutText("policyHeading", lang)}
        </CardTitle>
        <CardDescription>
          <span className="font-medium text-foreground" lang="en">
            {policy.title}
          </span>
          {" · "}
          {meta}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? aboutText("policyHide", lang) : aboutText("policyRead", lang)}
          </Button>
          <Button type="button" variant="outline" className="gap-1.5" onClick={() => void onDownload()} disabled={downloading}>
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {aboutText("policyDownload", lang)}
          </Button>
        </div>

        {open && (
          <div className="max-h-[28rem] space-y-4 overflow-y-auto rounded-md border bg-muted/30 p-4" lang="en">
            {englishNote && (
              <p className="text-xs text-muted-foreground" lang={lang}>
                {englishNote}
              </p>
            )}
            {policy.sections.map((s, i) => (
              <section key={i} className="space-y-1">
                {s.heading && <h3 className="text-sm font-semibold text-gray-900">{s.heading}</h3>}
                <p className="whitespace-pre-line text-sm text-gray-700">{s.body}</p>
              </section>
            ))}
          </div>
        )}

        {policy.acknowledged_at ? (
          <p className="flex items-center gap-2 text-sm text-green-700" data-testid="posh-about-acked">
            <CheckCircle2 aria-hidden className="h-4 w-4" />
            {aboutText("ackDone", lang, { date: formatDate(policy.acknowledged_at) })}
          </p>
        ) : (
          <Button type="button" onClick={onAck} disabled={ack.isPending} className="gap-1.5" data-testid="posh-about-ack">
            {ack.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {aboutText("ackButton", lang)}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function CommitteeCard({
  committee,
  lang,
}: Readonly<{ committee: PoshMyResponse["committee"]; lang: PoshLang }>) {
  return (
    <Card data-testid="posh-about-committee">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UsersRound aria-hidden className="h-4 w-4 text-primary" />
          {aboutText("committeeHeading", lang)}
        </CardTitle>
        {committee.length > 0 && <CardDescription>{aboutText("committeeIntro", lang)}</CardDescription>}
      </CardHeader>
      <CardContent>
        {committee.length === 0 ? (
          <p className="text-sm text-muted-foreground">{aboutText("noCommittee", lang)}</p>
        ) : (
          <ul className="divide-y">
            {committee.map((m, i) => (
              <li key={i} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{m.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {committeeRoleText(m.role, lang)}
                    {m.organization ? ` · ${m.organization}` : ""}
                  </p>
                </div>
                <div className="flex flex-col gap-1 text-sm sm:items-end">
                  {m.email && (
                    <a className="inline-flex items-center gap-1.5 break-all text-primary hover:underline [@media(pointer:coarse)]:min-h-11" href={`mailto:${m.email}`}>
                      <Mail aria-hidden className="h-3.5 w-3.5 shrink-0" />
                      {m.email}
                    </a>
                  )}
                  {m.phone && (
                    <a className="inline-flex items-center gap-1.5 text-primary hover:underline [@media(pointer:coarse)]:min-h-11" href={telHref(m.phone)}>
                      <Phone aria-hidden className="h-3.5 w-3.5 shrink-0" />
                      {m.phone}
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ConcernCard({ links, lang }: Readonly<{ links: PoshMyResponse["links"]; lang: PoshLang }>) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  return (
    <Card data-testid="posh-about-concern">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquareWarning aria-hidden className="h-4 w-4 text-primary" />
          {aboutText("concernHeading", lang)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
          <li>{aboutText("concernStep1", lang)}</li>
          <li>{aboutText("concernStep2", lang)}</li>
          <li>{aboutText("concernStep3", lang)}</li>
          <li>{aboutText("concernStep4", lang)}</li>
        </ol>
        <p className="text-xs text-muted-foreground">{aboutText("concernTime", lang)}</p>
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to="/app/$tenantSlug/posh/complaints/new" params={{ tenantSlug }}>
              {aboutText("concernFormButton", lang)}
            </Link>
          </Button>
          <Button asChild variant="outline" className="gap-1.5">
            <a href={links.shebox} target="_blank" rel="noopener noreferrer">
              {aboutText("concernSheBox", lang)}
              <ExternalLink aria-hidden className="h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
